import { useEffect } from 'react';
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_SOCIAL_IMAGE_PATH,
  DEFAULT_SITE_ORIGIN,
  DEFAULT_TITLE,
  buildDocumentTitle,
  resolveRobotsDirective,
  toAbsoluteUrl
} from '../lib/siteMeta';

/**
 * Hook to manage SEO meta tags, titles, and canonical URLs
 * @param {Object} config - SEO configuration
 * @param {string} config.title - Page title
 * @param {string} config.description - Meta description
 * @param {string} config.canonical - Canonical URL (without domain)
 * @param {string} config.robots - Robots meta content (default: 'index,follow')
 * @param {Object} config.openGraph - Open Graph configuration
 */
const useSEO = (config) => {
  const {
    title,
    description,
    canonical,
    robots = 'index,follow',
    openGraph = {}
  } = config;

  const setMetaTag = ({ selector, attribute, key, value }) => {
    if (!value) return;
    let tag = document.querySelector(selector);
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute(attribute, key);
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', value);
  };

  const setLinkTag = ({ selector, rel, href }) => {
    let linkTag = document.querySelector(selector);
    if (!linkTag) {
      linkTag = document.createElement('link');
      linkTag.setAttribute('rel', rel);
      document.head.appendChild(linkTag);
    }
    linkTag.setAttribute('href', href);
  };

  useEffect(() => {
    // Set page title
    document.title = buildDocumentTitle(title || DEFAULT_TITLE);

    // Set meta description
    let descriptionTag = document.querySelector("meta[name='description']");
    const resolvedDescription = description || DEFAULT_DESCRIPTION;
    if (resolvedDescription) {
      if (!descriptionTag) {
        descriptionTag = document.createElement('meta');
        descriptionTag.setAttribute('name', 'description');
        document.head.appendChild(descriptionTag);
      }
      descriptionTag.setAttribute('content', resolvedDescription);
    }

    // Set canonical URL
    const canonicalUrl = canonical ? toAbsoluteUrl(canonical) : `${DEFAULT_SITE_ORIGIN}${window.location.pathname}`;
    setLinkTag({ selector: "link[rel='canonical']", rel: 'canonical', href: canonicalUrl });

    // Set robots meta tag
    const resolvedRobots = resolveRobotsDirective(robots);

    let robotsTag = document.querySelector("meta[name='robots']");
    if (!robotsTag) {
      robotsTag = document.createElement('meta');
      robotsTag.setAttribute('name', 'robots');
      document.head.appendChild(robotsTag);
    }
    robotsTag.setAttribute('content', resolvedRobots);

    let googlebotTag = document.querySelector("meta[name='googlebot']");
    if (!googlebotTag) {
      googlebotTag = document.createElement('meta');
      googlebotTag.setAttribute('name', 'googlebot');
      document.head.appendChild(googlebotTag);
    }
    googlebotTag.setAttribute('content', resolvedRobots);

    // Set Open Graph tags
    const resolvedOg = {
      type: 'website',
      title: buildDocumentTitle(title || DEFAULT_TITLE),
      description: resolvedDescription,
      url: canonicalUrl,
      image: toAbsoluteUrl(DEFAULT_SOCIAL_IMAGE_PATH),
      ...openGraph,
    };

    Object.entries(resolvedOg).forEach(([key, value]) => {
      const content = value ? String(value) : '';
      if (!content) return;
      const property = key.startsWith('og:') ? key : `og:${key}`;
      setMetaTag({
        selector: `meta[property='${property}']`,
        attribute: 'property',
        key: property,
        value: content
      });
    });

    const resolvedTwitter = {
      card: 'summary_large_image',
      title: buildDocumentTitle(title || DEFAULT_TITLE),
      description: resolvedDescription,
      url: canonicalUrl,
      image: toAbsoluteUrl(DEFAULT_SOCIAL_IMAGE_PATH),
    };

    Object.entries(resolvedTwitter).forEach(([key, value]) => {
      const content = value ? String(value) : '';
      if (!content) return;
      const name = key.startsWith('twitter:') ? key : `twitter:${key}`;
      setMetaTag({
        selector: `meta[name='${name}'], meta[property='${name}']`,
        attribute: 'name',
        key: name,
        value: content
      });
    });

    // Signal to prerenderers that route-level SEO tags are in place.
    try {
      document.dispatchEvent(new Event('seo-ready'));
    } catch (error) {
      // ignore
    }

    // Cleanup function to restore defaults (optional - usually not needed for SPAs)
    return () => {
      document.title = DEFAULT_TITLE;
      if (descriptionTag) descriptionTag.setAttribute('content', DEFAULT_DESCRIPTION);
    };
  }, [title, description, canonical, robots, openGraph]);
};

export default useSEO;
